import { useState, useEffect } from "react";
import { PrimaryButton, SecondaryButton } from "@/components/StandardButton";
import { CheckCircle2, Copy, ExternalLink, Sparkles, Rocket, Lock, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import confetti from "canvas-confetti";
import { DisplayM, BodyText, SubsectionHeader, Caption } from "@/components/ui/typography";
import { generateFeedbackLink } from "@/utils/feedbackLinks";
import { supabase } from "@/integrations/supabase/client";

interface SuccessStepProps {
  pageSlug: string;
  companyName: string;
  logoUrl?: string;
  onComplete: () => void;
}

export const SuccessStep = ({ pageSlug, companyName, logoUrl, onComplete }: SuccessStepProps) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [passcodeCopied, setPasscodeCopied] = useState(false);
  const [passcode, setPasscode] = useState<string | null>(null);

  const feedbackUrl = generateFeedbackLink(companyName, pageSlug);

  useEffect(() => {
    confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
  }, []);

  useEffect(() => {
    const fetchPasscode = async () => {
      const { data } = await supabase
        .from("feedback_pages")
        .select("passcode")
        .eq("slug", pageSlug)
        .single();
      if (data?.passcode) setPasscode(data.passcode);
    };
    fetchPasscode();
  }, [pageSlug]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(feedbackUrl);
      setCopied(true);
      toast({ title: "Link copied!" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Failed to copy", variant: "destructive" });
    }
  };

  const handleCopyPasscode = async () => {
    if (!passcode) return;
    try {
      await navigator.clipboard.writeText(passcode);
      setPasscodeCopied(true);
      toast({ title: "Access code copied!" });
      setTimeout(() => setPasscodeCopied(false), 2000);
    } catch {
      toast({ title: "Failed to copy", variant: "destructive" });
    }
  };

  return (
    <div className="p-4 sm:p-8 text-center">
      <div className="max-w-xl mx-auto space-y-6">
        <div>
          {logoUrl ? (
            <div className="w-20 h-20 rounded-2xl border bg-card flex items-center justify-center mx-auto mb-6 overflow-hidden shadow-sm">
              <img
                src={logoUrl}
                alt={`${companyName} logo`}
                className="max-w-[80%] max-h-[80%] object-contain"
              />
            </div>
          ) : (
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Rocket className="w-10 h-10 text-success" />
            </div>
          )}
          <DisplayM className="mb-2">
            🎉 You're all set, {companyName}!
          </DisplayM>
          <BodyText className="text-muted-foreground">
            Your workspace is ready. Start collecting feedback today.
          </BodyText>
        </div>

        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 rounded-2xl p-5 border border-primary/20">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-primary" />
            <SubsectionHeader>Your Feedback Link</SubsectionHeader>
          </div>
          <div className="bg-background rounded-2xl p-3 border border-border mb-3 min-w-0">
            <code className="text-xs sm:text-sm break-all text-foreground">{feedbackUrl}</code>
          </div>

          {passcode && (
            <div className="bg-background rounded-2xl p-3 border border-primary/30 mb-4">
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <Lock className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Access Code:</span>
                <span className="font-mono font-bold text-primary text-lg">{passcode}</span>
                <SecondaryButton onClick={handleCopyPasscode} size="sm" className="ml-2 h-7 px-2">
                  {passcodeCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </SecondaryButton>
              </div>
              <Caption className="text-muted-foreground mt-1.5">
                Recipients need this code to submit feedback
              </Caption>
            </div>
          )}

          <div className="flex gap-2 justify-center flex-wrap">
            <SecondaryButton onClick={handleCopy} size="sm" className="gap-2">
              <Copy className="w-4 h-4" />
              {copied ? "Copied!" : "Copy Link"}
            </SecondaryButton>
            <SecondaryButton
              onClick={() => window.open(`${feedbackUrl}?preview=true`, '_blank')}
              size="sm"
              className="gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Preview
            </SecondaryButton>
          </div>
        </div>

        <div className="text-left bg-muted/50 rounded-2xl p-5">
          <h4 className="font-semibold text-center mb-4">What's next?</h4>
          <div className="space-y-3">
            <div className="flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Share your link {passcode && "& access code"}</div>
                <div className="text-sm text-muted-foreground">Send via email, Slack, or print the QR code</div>
              </div>
            </div>
            <div className="flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Collect anonymous feedback</div>
                <div className="text-sm text-muted-foreground">All responses are 100% anonymous</div>
              </div>
            </div>
            <div className="flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">View AI-powered insights</div>
                <div className="text-sm text-muted-foreground">Sentiment analysis, themes, and trends</div>
              </div>
            </div>
          </div>
        </div>

        <PrimaryButton onClick={onComplete} className="w-full min-h-[48px]">
          Go to Dashboard →
        </PrimaryButton>
      </div>
    </div>
  );
};
